import React, { useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

/**
 * Combobox - An autocomplete input component with suggestions
 *
 * @param {Array} suggestions - Array of suggestion strings
 * @param {string} value - Current input value
 * @param {Function} onChange - Callback when value changes (receives string value)
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Additional classes for the container
 * @param {boolean} disabled - Whether the input is disabled
 */
const Combobox = ({
  suggestions = [],
  value = "",
  onChange,
  placeholder,
  className = "",
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Filter suggestions based on input (case-insensitive)
  const filteredSuggestions = suggestions.filter((suggestion) =>
    suggestion.toLowerCase().includes(value.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen && e.key !== "Escape") {
        if (
          e.key === "ArrowDown" ||
          e.key === "ArrowUp" ||
          (e.key === "Enter" && filteredSuggestions.length > 0)
        ) {
          setIsOpen(true);
          return;
        }
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filteredSuggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredSuggestions.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]) {
            onChange(filteredSuggestions[highlightedIndex]);
            setIsOpen(false);
            setHighlightedIndex(-1);
          } else if (value.trim()) {
            // Accept free-form input
            setIsOpen(false);
            setHighlightedIndex(-1);
          }
          break;
        case "Escape":
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
        case "Tab":
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
        default:
          break;
      }
    },
    [isOpen, highlightedIndex, filteredSuggestions, onChange, value]
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleSuggestionClick = (suggestion) => {
    onChange(suggestion);
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (filteredSuggestions.length > 0) {
      setIsOpen(true);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  // Highlight matching text in suggestions
  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;

    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;

    const before = text.slice(0, index);
    const match = text.slice(index, index + query.length);
    const after = text.slice(index + query.length);

    return (
      <>
        {before}
        <span className="font-medium text-[#E07A3A]">{match}</span>
        {after}
      </>
    );
  };

  const dropdownVariants = {
    hidden: {
      opacity: 0,
      y: -8,
      scale: 0.98,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.15,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      y: -4,
      scale: 0.98,
      transition: {
        duration: 0.1,
        ease: "easeIn",
      },
    },
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input Field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder ?? t("analysis.combobox.placeholder")}
          disabled={disabled}
          className={`w-full rounded-[10px] border px-4 py-3 pr-10 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150 ${
            disabled
              ? "cursor-not-allowed border-[#E5E7EB] bg-[#F9FAFB] text-[#9CA3AF]"
              : "border-[#E5E7EB] bg-white"
          }`}
          style={{
            borderColor: isFocused && !disabled ? "#E07A3A" : "#E5E7EB",
            boxShadow:
              isFocused && !disabled
                ? "0 0 0 3px rgba(224, 122, 58, 0.1)"
                : "none",
          }}
        />
        <ChevronDown
          size={16}
          className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] transition-transform duration-150 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && filteredSuggestions.length > 0 && (
          <motion.div
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-[#F3F4F6] bg-white shadow-lg"
            style={{ maxHeight: "200px", overflowY: "auto" }}
          >
            <div className="py-1">
              {filteredSuggestions.map((suggestion, index) => {
                const isHighlighted = index === highlightedIndex;
                return (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`w-full px-3 py-2 text-left text-sm transition duration-150 ${
                      isHighlighted
                        ? "bg-[#FAFAFA] text-[#111827]"
                        : "text-[#374151] hover:bg-[#FAFAFA]"
                    }`}
                  >
                    {highlightMatch(suggestion, value)}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Combobox;
